import { Box, Paper, Popover, Typography, useTheme } from '@mui/material';
import { setPolygonMode, toggleCampaignH3DwellLayer, toggleEgeData } from '@slice';
import { RootState } from '@store';
import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import HexagonCompleteFilled from '@icons/HexagonCompleteFilled';
import HexagonOutlined from '@icons/HexagonOutlined';
import PointsFilled from '@icons/PointsFilled';
import PointsOutlined from '@icons/PointsOutlined';
import PolygonFilled from '@icons/PolygonFilled';
import PolygonOutlined from '@icons/PolygonOutlined';
import { BUTTON_COLORS } from '../../const';
import LayerToggleItem from './LayerToggleItem';

interface TouchstoneLayerPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const TouchstoneLayerPopover = ({ anchorEl, open, onClose }: TouchstoneLayerPopoverProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { polygonMode, isEgeDataOn, campaignH3DwellLayerEnabled, campaign } = useSelector(
    (state: RootState) => ({
      polygonMode: state.slice.polygonMode,
      isEgeDataOn: state.slice.isEgeDataOn,
      campaignH3DwellLayerEnabled: state.slice.campaignH3DwellLayerEnabled,
      campaign: state.slice.campaign
    }),
    shallowEqual
  );

  const onPolygonLayerToggle = useCallback(() => {
    dispatch(setPolygonMode(polygonMode === 'global' ? 'off' : 'global'));
  }, [dispatch, polygonMode]);

  const onCampaignPolygonToggle = useCallback(() => {
    dispatch(setPolygonMode(polygonMode === 'campaign' ? 'off' : 'campaign'));
  }, [dispatch, polygonMode]);

  const onEGELayerToggle = useCallback(() => {
    dispatch(toggleEgeData());
  }, [dispatch]);

  const onHexAggLayerToggle = useCallback(() => {
    dispatch(toggleCampaignH3DwellLayer());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  }, [onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      disableAutoFocus
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Paper sx={{ padding: theme.spacing(1, 0), display: 'flex', flexDirection: 'column', width: 260 }}>
        <Box sx={{ padding: theme.spacing(1, 2), gap: 0.5, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" color="text.primary">
            AdTech Layers
          </Typography>
          <Typography variant="body3" color="text.secondary">
            Toggle layers to show or hide additional details on the map.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'inline-grid',
            alignSelf: 'stretch',
            gridTemplateRows: 'repeat(4, fit-content(100%))',
            gridTemplateColumns: '1fr'
          }}
        >
          <LayerToggleItem
            gridRow="1 / span 1"
            gridColumn="1 / span 1"
            icon={polygonMode === 'global' ? <PolygonFilled fontSize="small" /> : <PolygonOutlined fontSize="small" />}
            label="Global Polygon"
            checked={polygonMode === 'global'}
            onChange={onPolygonLayerToggle}
            activeColor={BUTTON_COLORS.polygon.active}
          />
          <LayerToggleItem
            disabled={!campaign}
            gridRow="2 / span 1"
            gridColumn="1 / span 1"
            icon={polygonMode === 'campaign' ? <PolygonFilled fontSize="small" /> : <PolygonOutlined fontSize="small" />}
            label="Campaign Polygons"
            checked={polygonMode === 'campaign'}
            onChange={onCampaignPolygonToggle}
            activeColor={BUTTON_COLORS.polygon.active}
          />
          <LayerToggleItem
            disabled={!campaign}
            gridRow="3 / span 1"
            gridColumn="1 / span 1"
            icon={isEgeDataOn ? <PointsFilled fontSize="small" /> : <PointsOutlined fontSize="small" />}
            label="Campaign EGE"
            checked={isEgeDataOn}
            onChange={onEGELayerToggle}
            activeColor={BUTTON_COLORS.ege.active}
          />
          <LayerToggleItem
            gridRow="4 / span 1"
            gridColumn="1 / span 1"
            icon={campaignH3DwellLayerEnabled ? <HexagonCompleteFilled fontSize="small" /> : <HexagonOutlined fontSize="small" />}
            label="Hex Agg"
            checked={campaignH3DwellLayerEnabled}
            onChange={onHexAggLayerToggle}
            disabled={!campaign || !campaign.warming_identifier}
            activeColor={BUTTON_COLORS.hexagg.active}
          />
        </Box>
      </Paper>
    </Popover>
  );
};

export default TouchstoneLayerPopover;
